/// <amd-module name="Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/TestStepOM" />

// Based on code in \Tfs\Service\WebAccess\TestManagement\Scripts\TFS.TestManagement.ts

import * as Utils_String from "VSS/Core/Util/String";
import { HtmlSanitizer } from "roosterjs-react";
import { DalFields } from "Wit/Common/Generated/Constants";
import { Attachment, Link } from "Wit/WorkItemModel/WorkItem/Links";
import { IResourceLink } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import * as Wit_Utils_Array from "Wit/Common/Utilities/Array";
import { ArrayUtils as Utils_Array } from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/ArrayUtils";
import { HtmlUtils } from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/HtmlUtils";
import { ParameterCommonUtils } from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/ParameterCommonUtils";

var _domParser: DOMParser = new DOMParser(); //TODO: Pass the parser in with ctor?

/**
 * This is the date to use for end dates of active items.
 * Copied from Links.ts
 */
const futureDate = new Date(253370764800000);

export class TestActionTypes {
    public static Step: string = "Step";
    public static SharedSteps: string = "SharedSteps";
}

export class TestStepTypes {
    public static Action: string = "ActionStep";
    public static Validate: string = "ValidateStep";
}

export enum TestStepChangeType {
    Added,
    Deleted,
    Edited
}

export class TestAction {
    private _isDirty: boolean;

    public id: number;
    public actionType: string;
    public action: string;
    public index: number;
    public owner: any;

    constructor(id: number, actionType: string) {
        this.id = id;
        this.actionType = actionType;
        this.action = "";
        this._isDirty = false;
        this.owner = null;
    }

    public setIndex(index: number) {
        this.index = index;
    }

    public getIsDirty() {
        return this._isDirty;
    }

    public setIsDirty(isDirty: boolean) {
        this._isDirty = isDirty;
    }

    public hasError() {
        return false;
    }

    public appendTo($stepsXmlDom: Element): Element {
        return $stepsXmlDom;
    }

    public getAttachmentCount() {
        return 0;
    }

    public getAttachments(): Attachment[] {
        return [];
    }

    public processTestAttachments(attachmentList: Link[]): boolean {
        return false;
    }
}

export class ResourceLink implements IResourceLink {
    public AddedDate: Date;
    public AreaId: number;
    public Comment: string;
    public CreationDate: Date;
    public ExtId: number;
    public FilePath: string;
    public FilePathHash: number;
    public FldId: number;
    public LastWriteDate: Date;
    public Length: number;
    public OriginalName: string;
    public RemovedDate: Date;
    public WorkItemId: number;
}

export class TestStepAttachmentMetadata {
    private resource: ResourceLink;
    private uri: string;

    public getUri(): string {
        return this.uri;
    }

    public setUri(uri: string): void {
        this.uri = uri;
    }

    public getComment(): string {
        return this.resource.Comment;
    }

    public getOriginalName(): string {
        return this.resource.OriginalName;
    }

    public getFilePath(): string {
        return this.resource.FilePath;
    }

    public getLength(): number {
        return this.resource.Length;
    }

    public getWorkItemId(): number {
        return this.resource.WorkItemId;
    }

    public updateMetadatafromAttachment(attachment: Attachment) {
        this.resource.AddedDate = attachment.getAddedDate() ?? futureDate;
        this.resource.Comment = attachment.getComment() ?? "";
        this.resource.FilePath = attachment.linkData.FilePath ?? "";
        this.resource.FldId = attachment.getFieldId();
        // TODO: Fetch workItemId? Or Remove it?
        // this.resource.WorkItemId = attachment.workItem.id;
        this.resource.Length = attachment.linkData.Length ?? 0;
        this.resource.OriginalName = attachment.linkData.OriginalName ?? "";
        this.uri = "";
    }

    public updateMetadatafromResourceLink(resource: IResourceLink) {
        this.resource = resource;
    }

    constructor() {
        this.resource = new ResourceLink();
        this.uri = "";
    }
}

export class TestStep extends TestAction {
    public static createStep(id: number, stepType: string, action: string, expectedResult: string, isFormatted?: boolean) {
        /// <param name="isFormatted" type="boolean" optional="true" />

        let testStep = new TestStep(id, stepType, action, expectedResult, isFormatted);
        return testStep;
    }

    public stepType: string;
    public expectedResult: string;
    public isFormatted: boolean;
    private _hasActionError: boolean;
    private _updateOwner: boolean;
    private _hasExpectedResultError: boolean;
    private _actionParameters: string[];
    private _expectedParameters: string[];
    private _attachments: Attachment[];
    private _attachmentsMetadata: TestStepAttachmentMetadata[];

    constructor(id: number, stepType: string, action: string, expectedResult: string, isFormatted?: boolean) {
        /// <param name="isFormatted" type="boolean" optional="true" />
        let parameters: string[];
        super(id, TestActionTypes.Step);
        this._actionParameters = [];
        this._expectedParameters = [];
        this.stepType = stepType;

        this.action = action;
        this._updateParameters(ParameterCommonUtils.getParameters(action), this._actionParameters);

        this.expectedResult = expectedResult;
        this._updateParameters(ParameterCommonUtils.getParameters(expectedResult), this._expectedParameters);

        this.isFormatted = isFormatted ?? false;
        this._hasActionError = false;
        this._hasExpectedResultError = false;
        this._attachments = [];
        this._attachmentsMetadata = [];
        this._updateOwner = true;
    }

    public setAction(action: string): boolean {
        let parameters;
        if (this.action !== action) {
            parameters = ParameterCommonUtils.getParameters(action);
            this._hasActionError = parameters.length > 0;
            if (this._updateParameters(parameters, this._actionParameters)) {
                // If the user does not confirm in case of deletion of the last reference of a parameter, the changes done by the user should be undone.
                // This can be done by not setting the action in this case.
                this.action = action;
                this.setIsDirty(true);
                return true;
            }
        }
        return false;
    }

    public setExpectedResult(expectedResult: string): boolean {
        let parameters;
        if (this.expectedResult !== expectedResult) {
            parameters = ParameterCommonUtils.getParameters(expectedResult);
            this._hasExpectedResultError = parameters.length > 0;
            if (this._updateParameters(parameters, this._expectedParameters)) {
                this.expectedResult = expectedResult;
                this.setIsDirty(true);
                if (!this._isExpectedResultEmpty()) {
                    this.stepType = TestStepTypes.Validate;
                } else {
                    this.stepType = TestStepTypes.Action;
                }
                return true;
            }
        }
        return false;
    }

    public hasError() {
        return this._hasActionError || this._hasExpectedResultError;
    }

    public hasActionError() {
        return this._hasActionError;
    }

    public hasExpectedResultError() {
        return this._hasExpectedResultError;
    }

    public getActionParameters(): string[] {
        return this._actionParameters;
    }

    public getExpectedParameters(): string[] {
        return this._expectedParameters;
    }

    public appendTo($stepsXmlDom: Element): Element {
        let stepXml = _domParser.parseFromString("<step></step>", "application/xml"),
            $stepXml = stepXml.querySelector("step"),
            descriptionXml = _domParser.parseFromString("<description></description>", "application/xml"),
            $descriptionXml = descriptionXml.querySelector("description");

        $stepXml?.setAttribute("id", this.id.toString());
        if (!this._isExpectedResultEmpty()) {
            $stepXml?.setAttribute("type", TestStepTypes.Validate);
        } else {
            $stepXml?.setAttribute("type", TestStepTypes.Action);
        }

        this._addParameterizedString($stepXml, this.action);
        this._addParameterizedString($stepXml, this.expectedResult);
        if ($descriptionXml) {
            $stepXml?.append($descriptionXml);
        }

        if ($stepXml) {
            $stepsXmlDom.append($stepXml);
        }
        return $stepsXmlDom;
    }

    public processTestAttachments(attachmentList: Link[], instanceOfAttachmentCheck: boolean = true): boolean {
        let metaTag = Utils_String.format("[teststep={0}]", this.id),
            i,
            comment,
            commentMetaTag,
            attachment,
            attachmentFound = false,
            length: number;

        // If there are attachements, look for ones that belong to this test step.
        // To figure out if it belongs to the step, look at the comment for the
        // attachment and see if it starts with something like [TestStep=1].
        if (attachmentList) {
            length = attachmentList.length;
            for (i = 0; i < length; i++) {
                comment = attachmentList[i].linkData.Comment;
                if (comment && comment.length > metaTag.length) {
                    commentMetaTag = comment.substr(0, metaTag.length).toLowerCase();
                    if (commentMetaTag === metaTag) {
                        if (
                            (!instanceOfAttachmentCheck && attachmentList[i].linkData.FldID === DalFields.AttachedFiles) ||
                            attachmentList[i] instanceof Attachment
                        ) {
                            this.addAttachment(<Attachment>attachmentList[i]);
                            attachmentFound = true;
                        }
                    }
                }
            }
        }

        return attachmentFound;
    }

    public getAttachmentCount(): number {
        return this._attachments ? this._attachments.length : 0;
    }

    public getAttachments(): Attachment[] {
        return this._attachments;
    }

    public getAttachmentsMetadataList(): TestStepAttachmentMetadata[] {
        return this._attachmentsMetadata;
    }

    public deleteAttachment(attachment: Attachment): boolean {
        let isItemRemoved: boolean = false;
        if (attachment) {
            isItemRemoved = Utils_Array.remove(this.getAttachments(), attachment);
            if (isItemRemoved) {
                attachment.remove();
            }
        }
        return isItemRemoved;
    }

    public clearAttachments(): void {
        let stepAttachments: Attachment[] = Utils_Array.clone(this.getAttachments()),
            i: number,
            stepAttachmentCount: number = stepAttachments.length;

        for (i = 0; i < stepAttachmentCount; i++) {
            this.deleteAttachment(stepAttachments[i]);
        }
    }

    public addAttachment(attachment: Attachment) {
        this._attachments.push(attachment);
        this._addAttachmentMetadata(attachment);
    }

    private _addAttachmentMetadata(attachment: Attachment) {
        let attachmentMetadata: TestStepAttachmentMetadata = new TestStepAttachmentMetadata();

        attachmentMetadata.updateMetadatafromAttachment(attachment);
        this._attachmentsMetadata.push(attachmentMetadata);
    }

    public updateAttachmentsMetadataList(attachmentMetadata: TestStepAttachmentMetadata) {
        this._attachmentsMetadata.push(attachmentMetadata);
    }

    private _addParameterizedString($stepXml: Element | null, paramString: string) {
        let contentToWrite: string, paramStringXml: Document, xmlString: string, $paramStringXml: Element;

        if (paramString) {
            contentToWrite = HtmlSanitizer.sanitizeHtml(paramString);
        } else {
            contentToWrite = HtmlSanitizer.sanitizeHtml("");
        }

        if (!this.isFormatted) {
            contentToWrite = HtmlUtils.replaceNewLineWithBr(contentToWrite);
        }
        // TODO: Check whether React is doing the encoding
        // xmlString = Utils_String.htmlEncode(contentToWrite).replace(/&nbsp;/g, "&amp;nbsp;");
        xmlString = contentToWrite.replace(/&nbsp;/g, "&amp;nbsp;");
        paramStringXml = _domParser.parseFromString(
            Utils_String.format("<parameterizedString>{0}</parameterizedString>", xmlString),
            "application/xml"
        );
        $paramStringXml = paramStringXml.querySelector("parameterizedString")!;
        $paramStringXml.setAttribute("isformatted", "true");
        $stepXml?.append($paramStringXml);
    }

    // TODO: This func doesn't seem to be used. Delete it?
    // private _getContentToWrite(paramString: string) {
    //     let contentToWrite = HtmlNormalizer.normalize(paramString),
    //         innerHtml = /<div>([\S\s]*?)<\/div>/i.exec(paramString);

    //     if (innerHtml === null) {
    //         return "<div>" + paramString + "</div>";
    //     }
    //     else {
    //         return paramString;
    //     }
    // }

    private _isExpectedResultEmpty(): boolean {
        let expectedResultText: string;
        if (!this.expectedResult) {
            return true;
        }

        return HtmlUtils.isEmptyText(this.expectedResult);
    }

    private _updateParameters(newParamList: string[], targetParamList: string[]): boolean {
        // Updates the targetParamList according to the new paramList. And for all the updates(adds/deletes), fires the changes in the testcase.
        // Returns true if the update was successful. The update could be unsuccessful if on deletion of the last reference of a parameter, the user does not confirm.
        let i: number,
            deletedItems: string[] = [],
            addedItems: string[] = [],
            length: number;

        var item;
        for (i = 0; i < targetParamList.length; i++) {
            item = targetParamList[i];
            if (!Wit_Utils_Array.contains(newParamList, item, Utils_String.localeIgnoreCaseComparer)) {
                deletedItems.push(item);
            }
        }

        for (i = 0; i < newParamList.length; i++) {
            item = newParamList[i];
            if (!Wit_Utils_Array.contains(targetParamList, item, Utils_String.localeIgnoreCaseComparer)) {
                addedItems.push(item);
            }
        }

        // Delete items present in deletedItems from the targetParamList
        if (deletedItems.length > 0 && this.owner) {
            if (!this.owner.fireParamDeleteEvent(deletedItems)) {
                return false;
            }
        }

        this._deleteParametersFromList(deletedItems, targetParamList);

        // Add the new items to the targetParamList
        this._addParametersToList(addedItems, targetParamList);
        return true;
    }

    private _addParametersToList(paramNames: string[], paramList: string[]) {
        let length = paramNames.length,
            i: number;

        for (i = 0; i < length; i++) {
            if (!Wit_Utils_Array.contains(paramList, paramNames[i], Utils_String.localeIgnoreCaseComparer)) {
                paramList.push(paramNames[i]);
                // Update the parameter in testcase.
                if (this.owner && this._updateOwner) {
                    this.owner.onAddParameterInSteps(paramNames[i]);
                }
            }
        }
    }

    private _deleteParametersFromList(paramNames: string[], paramList: string[]) {
        let index: number,
            i: number,
            j: number,
            paramListLength = paramList.length,
            length = paramNames.length;

        for (i = 0; i < length; i++) {
            for (j = 0; j < paramListLength; j++) {
                if (Utils_String.localeIgnoreCaseComparer(paramNames[i], paramList[j]) === 0) {
                    paramList.splice(j, 1);
                    j++;
                    // Update the parameter in testcase.
                    if (this.owner && this._updateOwner) {
                        this.owner.onDeleteParameterInSteps(paramNames[i]);
                    }
                }
            }
        }
    }

    public deleteParameter(paramName: string) {
        let action = ParameterCommonUtils.renameParameterInString(this.action, paramName, ""),
            expected = ParameterCommonUtils.renameParameterInString(this.expectedResult, paramName, "");
        // setAction and setExpectedResult will update the parameter arrays for the step.
        this.setAction(action);
        this.setExpectedResult(expected);
    }

    public renameParameter(paramName: string, newParamName: string) {
        let action = ParameterCommonUtils.renameParameterInString(this.action, paramName, newParamName),
            expected = ParameterCommonUtils.renameParameterInString(this.expectedResult, paramName, newParamName);
        // In case of rename there is no need to update owner's refCounts. The refCounts will remain same in case of rename.
        this._updateOwner = false;
        this.setAction(action);
        this.setExpectedResult(expected);
        this._updateOwner = true;
    }
}

// TODO: Commented part should be implemented when we decide to make our own test steps table control.
export class SharedSteps extends TestAction {
    public static createSharedStep(id: number, ref: number) {
        return new SharedSteps(id, ref);
    }

    public ref: number;

    constructor(id: number, ref: number) {
        super(id, TestActionTypes.SharedSteps);
        this.ref = ref;
        // this._sharedStepWorkItem = null;
    }

/*
    public setSharedStepWorkItem(sharedStepWorkItem: SharedStepWorkItem) {
        this._sharedStepWorkItem = sharedStepWorkItem;
    }

    public preSave() {
        this._sharedStepWorkItem.preSave();
    }

    public appendTo($stepsXmlDom: Element): Element {
        let sharedStepRefXml = _domParser.parseFromString("<compref></compref>", "application/xml"),
            $sharedStepRefXml = sharedStepRefXml.querySelector("compref");

        $sharedStepRefXml!.setAttribute("id", this.id.toString());
        $sharedStepRefXml!.setAttribute("ref", this.ref.toString());

        $stepsXmlDom.append($sharedStepRefXml!);
        return $sharedStepRefXml!;
    }

    public getStep(id): TestStep {
        return this._sharedStepWorkItem.getStep(id);
    }

    public getTestSteps(): TestAction[] {
        return this._sharedStepWorkItem.getTestSteps();
    }

    public removeStep(id, parentId): boolean {
        return this._sharedStepWorkItem.removeStep(id, parentId);
    }

    public insertStep(id, parentId): number {
        return this._sharedStepWorkItem.insertStep(id, parentId);
    }

    public getIsDirty() {
        if (this._sharedStepWorkItem) {
            return this._sharedStepWorkItem.getIsDirty();
        }
        return false;
    }

    public setIsDirty(isDirty: boolean) {
        if (this._sharedStepWorkItem) {
            this._sharedStepWorkItem.setIsDirty(isDirty);
        }
    }

    public getSharedStep() {
        return this._sharedStepWorkItem;
    }

    public getTitle() {
        if (this._sharedStepWorkItem) {
            return this._sharedStepWorkItem.getTitle();
        }
    }

    public setTitle(title: string): boolean {
        if (this._sharedStepWorkItem) {
            return this._sharedStepWorkItem.setTitle(title);
        }
    }

    public hasError(): boolean {
        if (this._sharedStepWorkItem) {
            return this._sharedStepWorkItem.hasError();
        }
        return false;
    }

    public refreshTestStepsFromWorkItem() {
        if (this._sharedStepWorkItem) {
            this._sharedStepWorkItem.refreshTestStepsFromWorkItem();
        }
    }

    public processTestAttachments(attachmentList): boolean {
        if (this._sharedStepWorkItem) {
            return this._sharedStepWorkItem.processTestStepAttachments();
        }
    }

    private _sharedStepWorkItem: SharedStepWorkItem;
*/
}
/*
VSS.initClassPrototype(SharedSteps, {
    ref: null
});
*/
